import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { buildCommercialMessage, openWhatsApp } from "@/lib/constants";

const EMPTY = { business: "", contact: "", phone: "", address: "", requirement: "" };

const inputCls =
  "h-12 rounded-none border-[#B08D2E]/20 bg-[#F9F6F0]/60 px-4 text-[#1A1A1A] placeholder:text-[#6E675C]/50 focus-visible:ring-1 focus-visible:ring-[#B08D2E] focus-visible:ring-offset-0";

const labelCls =
  "mb-2 block text-[10px] font-medium uppercase tracking-[0.3em] text-[#6E675C]";

export default function CommercialModal({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !form.business.trim() ||
      !form.contact.trim() ||
      !form.phone.trim() ||
      !form.address.trim() ||
      !form.requirement.trim()
    ) {
      toast.error("Please complete every field before sending.");
      return;
    }
    openWhatsApp(buildCommercialMessage(form));
    toast.success("Opening WhatsApp with your enquiry details…");
    onOpenChange(false);
    setForm(EMPTY);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="commercial-modal"
        className="max-h-[92vh] w-[calc(100vw-1.5rem)] overflow-y-auto border-[#B08D2E]/25 bg-white p-5 text-[#1A1A1A] sm:w-full sm:max-w-xl sm:p-6"
      >
        <DialogHeader>
          <p className="text-[10px] font-medium uppercase tracking-[0.4em] text-[#B08D2E]">
            Commercial &amp; Hotels
          </p>
          <DialogTitle className="font-display text-3xl font-light text-[#1A1A1A]">
            Business <em className="gold-text font-medium italic">Enquiry</em>
          </DialogTitle>
          <DialogDescription className="text-sm font-light text-[#6E675C]">
            Tell us about your business — we will open WhatsApp with your
            enquiry written and ready to send.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-2 space-y-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="commercial-business" className={labelCls}>
                Business / Hotel Name
              </label>
              <Input
                id="commercial-business"
                data-testid="commercial-business-input"
                value={form.business}
                onChange={set("business")}
                placeholder="e.g. The Grand Hotel"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="commercial-contact" className={labelCls}>
                Contact Person Name
              </label>
              <Input
                id="commercial-contact"
                data-testid="commercial-contact-input"
                value={form.contact}
                onChange={set("contact")}
                placeholder="Your full name"
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="commercial-phone" className={labelCls}>
                Phone Number
              </label>
              <Input
                id="commercial-phone"
                data-testid="commercial-phone-input"
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="e.g. 07123 456789"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="commercial-address" className={labelCls}>
                Business Address / Location
              </label>
              <Input
                id="commercial-address"
                data-testid="commercial-address-input"
                value={form.address}
                onChange={set("address")}
                placeholder="Street, town and postcode"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label htmlFor="commercial-requirement" className={labelCls}>
              Approximate Requirement / Message
            </label>
            <Textarea
              id="commercial-requirement"
              data-testid="commercial-requirement-input"
              value={form.requirement}
              onChange={set("requirement")}
              placeholder="e.g. Weekly linen service for a 20-room hotel, staff uniform pressing twice a week…"
              className={`${inputCls} min-h-[110px] py-3`}
            />
          </div>

          <button
            type="submit"
            data-testid="commercial-submit-button"
            className="group relative flex w-full items-center justify-center gap-3 overflow-hidden bg-[#B08D2E] py-4 text-xs font-semibold uppercase tracking-[0.28em] text-[#F9F6F0] transition-colors duration-300 hover:bg-[#96771F]"
          >
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
            <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
            Send Enquiry via WhatsApp
          </button>
          <p className="pb-1 text-center text-[11px] font-light tracking-wide text-[#6E675C]/70">
            Your enquiry opens in WhatsApp to +44 7568 487789 — nothing is
            stored on this site.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
